import { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import KpiReportLayout from '@/components/reports/KpiReportLayout'
import {
  APR_ACK_DETAIL_COLUMNS,
  APR_CHILD_CLOSE_DETAIL_COLUMNS,
  APR_PARENT_CHILD_DETAIL_COLUMNS,
  buildAprSummaryRows,
  formatFilterRange,
} from '@/components/reports/kpiReportColumns'
import { helpdeskReportService, type AprDurationsResponse, type ReportFilters } from '@/lib/services/helpdeskReportService'

const emptyData: AprDurationsResponse = {
  summary: {},
  metric_breakdown: [],
  details: {},
  filters: {},
}

export default function AprDurationsReportPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [data, setData] = useState<AprDurationsResponse>(emptyData)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getAprDurations(filters)
      .then(setData)
      .catch(() => setData(emptyData))
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id])

  const periodLabel = formatFilterRange(data.filters ?? filters)

  const detailTabs = useMemo(
    () => [
      {
        label: 'Acknowledgements',
        columns: APR_ACK_DETAIL_COLUMNS,
        rows: data.details?.acknowledgements?.rows ?? [],
        total: data.details?.acknowledgements?.total ?? 0,
        shown: data.details?.acknowledgements?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.request_id ?? index),
      },
      {
        label: 'Parent → first child',
        columns: APR_PARENT_CHILD_DETAIL_COLUMNS,
        rows: data.details?.parent_to_first_child?.rows ?? [],
        total: data.details?.parent_to_first_child?.total ?? 0,
        shown: data.details?.parent_to_first_child?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.ticket_id ?? index),
      },
      {
        label: 'Child → parent close',
        columns: APR_CHILD_CLOSE_DETAIL_COLUMNS,
        rows: data.details?.child_to_parent_close?.rows ?? [],
        total: data.details?.child_to_parent_close?.total ?? 0,
        shown: data.details?.child_to_parent_close?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.ticket_id ?? index),
      },
    ],
    [data.details]
  )

  return (
    <Box p={4}>
      <ReportPageHeader
        title="APR Durations"
        subtitle="Average processing durations across the ticket lifecycle"
        reportType="apr-durations"
        filters={filters}
        onFiltersChange={setFilters}
      />
      <KpiReportLayout
        loading={loading}
        periodLabel={periodLabel}
        summaryRows={buildAprSummaryRows(data.summary)}
        breakdownRows={data.metric_breakdown ?? []}
        detailTabs={detailTabs}
      />
    </Box>
  )
}
